'use client';

import { Fragment } from 'react';
import { useRouter } from 'next/navigation';
import { useCountry } from '@/hooks/useCountry';
import { countryPath } from '@/lib/paths';
import type { VariantGroup } from '@/lib/variants';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface NavigationSelectProps {
  groups: VariantGroup[];
}

// Mobile variant picker: one select per attribute, the current variant selected,
// choosing an option navigates to that variant's own page (same country).
const NavigationSelect = ({ groups }: NavigationSelectProps) => {
  const router = useRouter();
  const country = useCountry();

  const handleValueChange = (slug: string) => {
    if (slug) {
      router.push(countryPath(country, `/product/${slug}`));
    }
  };

  return (
    <Fragment>
      {groups.map((group) => {
        const selected = group.options.find((option) => option.selected);
        return (
          <div key={group.type} className="w-full mb-2">
            <Select value={selected?.slug} onValueChange={handleValueChange}>
              <SelectTrigger className="w-full">
                <div className="flex gap-2">
                  <span className="font-semibold">{group.type}</span>
                  {/* Explicit label so the selected variant is in the server HTML too */}
                  <SelectValue placeholder="Choose">{selected?.label}</SelectValue>
                </div>
              </SelectTrigger>
              <SelectContent>
                {group.options.map((option) => (
                  <SelectItem
                    key={`${option.slug}:${option.label}`}
                    value={option.slug}
                    className={option.available ? undefined : 'text-muted-foreground'}
                  >
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );
      })}
    </Fragment>
  );
};

export default NavigationSelect;
